/** React imports */

/** Iconify imports */
import { Icon } from '@iconify/react'
import { Box, CircularProgress, Grid } from '@mui/material'
/** MUI imports */
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { PropsWithChildren, ReactNode } from 'react'

/** Component imports */
import { Button } from 'src/components/atoms'
/** Constant imports */
import { buttonColorStatusMapping, confirmationIconMapping, feedbackIconMapping } from '../constans/common'
/** Type imports */
import { BaseModalProps, BaseModalType, ConfirmationStatus, FeedbackStatus } from '../types/Modal.type'

/**
 * Modal Component
 *
 * A versatile modal component that can be customized for various use cases such as confirmation, feedback, and default dialogs.
 *
 * @param {PropsWithChildren<BaseModalProps<T>>} props - The props for the Modal component.
 * @param {boolean} [props.isOpen=false] - Controls whether the modal is open.
 * @param {() => void} props.onClose - Callback function to be called when the modal is closed.
 * @param {() => void} props.onOk - Callback function to be called when the positive action button is clicked.
 * @param {string} [props.positiveLabel='Submit'] - Label for the positive action button.
 * @param {string} [props.negativeLabel='Cancel'] - Label for the negative action button.
 * @param {string} props.title - Title of the modal.
 * @param {string} [props.description=''] - Description text for the modal.
 * @param {'left' | 'center' | 'right'} [props.position='left'] - Position of the content inside the modal.
 * @param {'primary' | 'secondary' | 'default'} [props.color='primary'] - Color theme of the modal.
 * @param {boolean} [props.closeable=true] - Determines if the modal can be closed by clicking an 'X' button.
 * @param {string} [props.icon='confirm'] - Icon type to be displayed in the modal.
 * @param {string} props.maxWidth - Maximum width of the modal.
 * @param {'paper' | 'body' | 'unset'} props.scroll - Determines the scroll behavior for the modal.
 * @param {ReactNode} props.children - Children elements to be rendered inside the modal.
 * @param {boolean} [props.renderAction=true] - Determines if the action buttons should be rendered.
 * @param {boolean} [props.loading=false] - Indicates if the modal is in a loading state.
 * @param {'right' | 'center'} [props.positionActionButton='right'] - Position of the action buttons.
 * @param {'small' | 'medium' | 'large'} [props.size='medium'] - Size of the modal.
 * @param {'default' | 'confirmation' | 'feedback'} [props.type='default'] - Type of the modal.
 * @param {FeedbackStatus | ConfirmationStatus} props.status - Status of the modal for determining icon and color.
 *
 * @returns {JSX.Element} The rendered modal component.
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   onOk={handleOk}
 *   title="Confirm Action"
 *   description="Are you sure you want to proceed?"
 *   type="confirmation"
 *   status="danger"
 * />
 * ```
 */
export const Modal = <T extends BaseModalType>({
  isOpen = false,
  onClose,
  onOk,
  positiveLabel = 'Submit',
  negativeLabel = 'Cancel',
  title,
  description = '',
  position = 'left',
  color = 'primary',
  closeable = true,
  maxWidth,
  scroll,
  children,
  renderAction = true,
  loading = false,
  positionActionButton = 'right',
  size = 'medium',
  type = 'default',
  status,
  customButtonAction,
  ...rest
}: PropsWithChildren<BaseModalProps<T>> & { customButtonAction?: ReactNode }) => {
  const { palette, colorToken } = useTheme()
  const colorStatus = (status as FeedbackStatus) || (status as ConfirmationStatus)

  const handleStyleDialog = () => {
    let fixedMaxWidth = '100%'

    if (size === 'medium') fixedMaxWidth = '580px'
    if (size === 'small') fixedMaxWidth = '360px'
    if (size === 'large') fixedMaxWidth = '800px'
    if (size === 'xl') fixedMaxWidth = '1080px'
    if (size === 'xxl') fixedMaxWidth = '90%'

    const style = {
      '& .MuiDialog-paper': {
        maxWidth: maxWidth ?? fixedMaxWidth,
        margin: size === 'fullScreen' ? '0' : 'auto'
      }
    }

    return style
  }

  const handleSizeCloseButton = () => {
    switch (size) {
      case 'small':
        return 'small'
      case 'medium':
        return 'medium'
      default:
        return 'large'
    }
  }

  const handleStyleActionBtn = () => {
    const style = {
      justifyContent: !positionActionButton || positionActionButton === 'right' ? 'flex-end' : 'center'
    }

    return style
  }

  const renderIcon = () => {
    const iconName =
      type === 'feedback'
        ? feedbackIconMapping[status as FeedbackStatus]
        : confirmationIconMapping[status as ConfirmationStatus]

    if (!iconName || type === 'default') return null

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: `${colorStatus}.50`,
          width: '64px',
          height: '64px',
          borderRadius: '64px',
          minWidth: '64px'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: `${colorStatus}.200`,
            width: '40px',
            height: '40px',
            borderRadius: '40px'
          }}
        >
          <Icon icon={iconName} fontSize={type === 'feedback' ? '37px' : '40px'} color={palette[colorStatus]?.main} />
        </Box>
      </Box>
    )
  }

  const renderContent = () => {
    if (rest.isLoadingGetDetail) {
      return (
        <Grid container justifyContent={'center'}>
          <CircularProgress />
        </Grid>
      )
    }

    return (
      <Box sx={{ display: 'grid', rowGap: '20px' }}>
        <Stack direction={position === 'left' ? 'row' : 'column'} justifyContent={position} alignItems='center' gap={2}>
          {renderIcon()}
          <Stack alignItems={position === 'center' ? position : 'start'} textAlign={position}>
            <Typography variant='h5'>{title}</Typography>
            <Typography mt='2px' variant='labelMd'>
              {description}
            </Typography>
          </Stack>
        </Stack>
        {children}
      </Box>
    )
  }

  const renderButtonAction = () => {
    return (
      <Stack width='100%' sx={handleStyleActionBtn()} direction='row' spacing='10px'>
        <Button
          content='textOnly'
          text={negativeLabel}
          color='primary'
          variant='outlined'
          sx={{ height: '36px' }}
          onClick={e => onClose?.(e)}
        />
        <Button
          variant='solid'
          content='textOnly'
          disabled={loading || rest?.isLoadingGetDetail}
          color={type === 'confirmation' ? buttonColorStatusMapping[colorStatus] : 'primary'}
          sx={{
            height: '36px'
          }}
          text={loading ? 'Loading...' : positiveLabel}
          onClick={e => onOk?.(e)}
        />
      </Stack>
    )
  }

  return (
    <Dialog
      open={isOpen}
      scroll={scroll}
      fullWidth
      sx={handleStyleDialog()}
      fullScreen={rest?.fullScreen || size === 'fullScreen' || false}
      {...(closeable ? { onClose } : {})}
    >
      {closeable && (
        <IconButton
          size={handleSizeCloseButton()}
          sx={{
            position: 'absolute',
            right: '0.8rem',
            top: '0.8rem',
            backgroundColor: colorToken.background.neutral.normal,
            zIndex: 99999
          }}
          onClick={onClose}
        >
          <Icon icon='tabler:x' />
        </IconButton>
      )}

      {JSON.stringify(rest.fullScreen)}

      <DialogContent sx={{ mt: closeable ? 3 : 0 }}>{renderContent()}</DialogContent>
      {renderAction && <DialogActions>{customButtonAction || renderButtonAction()}</DialogActions>}
    </Dialog>
  )
}
